import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BackboneComponent } from '@components/backbone/backbone.component';
import { BreadcrumbComponent } from '@components/breadcrumb/breadcrumb.component';
import { PlaceholderTable } from '@components/placeholders/table/table.component';
import { ExcelService } from '@services/excel.service';
import { ReportService } from '@services/report.service';
import { UserService } from '@services/user.service';
import { SessionService } from '@utils/session.service';
import { ToastService } from '@utils/toast.service';
import { UtilsService } from '@utils/utils.service';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { IconFieldModule } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ShowForRolesDirective } from '@directives/show-for-roles.directive';
import { ShowForPermissionsDirective } from '@directives/show-for-permissions.directive';
import { InputGroup } from 'primeng/inputgroup';

@Component({
  selector: 'app-report',
  imports: [
    CommonModule,
    BackboneComponent,
    BreadcrumbComponent,
    TableModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    FormsModule,
    InputGroupAddonModule,
    ButtonModule,
    PlaceholderTable,
    DrawerModule,
    IftaLabelModule,
    SelectModule,
    ShowForRolesDirective,
    InputGroup,
    ShowForPermissionsDirective,
  ],
  templateUrl: './report-self.component.html',
  styleUrl: './report-self.component.scss',
})
export class ReportSelfPage {
  private excelService = inject(ExcelService);
  private reportService = inject(ReportService);
  private utils = inject(UtilsService);
  private sessionService = inject(SessionService);
  private userService = inject(UserService);
  private toastService = inject(ToastService);

  public isLoading: boolean = false;
  public drawerNewReportVisible: boolean = false;

  public searchValue: string | undefined;
  public periodSelected: any = null;
  public report: any = [];

  public periodsList: any = [];
  public listUsers: any = [];
  public totalPrice: number = 0;
  public totalActions: any = [];

  public ngOnInit() {
    this.isLoading = true;

    let userId = this.sessionService.getUser()?.userId;

    this.userService.getUsersOfUser(Number(userId)).then((response: any) => {
      if (this.sessionService.getRole() === 'GESTOR') {
        this.listUsers = response.map((user: any) => ({ label: user.nickname, value: user.userId }));
      } else {
        this.listUsers = response.childrens.map((user: any) => ({ label: user.nickname, value: user.userId }));
      }

      this.listUsers = this.utils.orderArrayBy(this.listUsers, 'label');
    });

    this.searchReport();

    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Los meses son 0-indexados
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    let dates = this.getLastFourMondays();

    for (let i = 0; i < dates.length; i++) {
      const date = dates[i];
      let period = this.getWeekDateRange(date);
      let label = '';

      // Verificar si es el primer elemento
      if (i === 0) {
        label = `Periodo actual`;
      } else if (i === 1) {
        label = `Periodo anterior`;
      } else {
        label = 'Perido del';
      }

      this.periodsList.push({ label: label, value: formatDate(date), period: period });
    }
  }

  searchReport = (): void => {
    this.isLoading = true;

    this.reportService.myReportList(this.periodSelected).then((response: any) => {
      this.report = this.utils.orderArrayBy(response.content, 'fecha', 'desc').map((item: any) => ({
        ...item,
        isEditing: false,
        reassign: null,
      }));

      this.totalPrice = response.totalPrice;
      this.totalActions = response.actions;
      this.isLoading = false;
    });
  };

  reassignUser = (item: any): void => {
    if (!item.reassign) {
      this.toastService.error('Debe seleccionar un usuario para reasignar.');
      return;
    }

    let body = {
      module: item.type,
      id: Number(item.id),
      userId: Number(item.userId),
      reassignUserId: Number(item.reassign),
    };

    this.reportService.reassign(body).then((response: any) => {
      let user = this.listUsers.find((user: any) => user.value === response.userId);
      if (user) {
        item.usuario = user.label;
        item.userId = user.value;
        item.isEditing = false;
        item.reassign = null;

        this.toastService.success('Usuario reasignado correctamente.');
      }
    });
  };

  exportToExcel = (): void => {
    const report: any = [];

    this.report.forEach((item: any) => {
      let data: {
        USUARIO: string;
        FECHA: string;
        ESTADO: string;
        DATO: string;
        DOCUMENTO: string;
        PRECIO?: string;
        PROVEEDOR?: string;
      } = {
        USUARIO: item.usuario,
        FECHA: item.fecha,
        ESTADO: item.estado,
        DATO: item.dato,
        DOCUMENTO: item.documento,
        PROVEEDOR: item.provider,
      };

      if (this.sessionService.getRole() === 'SUPERUSER' || this.sessionService.getRole() === 'COLABORADOR') {
        data.PRECIO = item.precio;
      }

      report.push(data);
    });

    var today = new Date();
    var dd = String(today.getDate()).padStart(2, '0');
    var mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
    var yyyy = today.getFullYear();
    var hh = String(today.getHours()).padStart(2, '0');
    var ii = String(today.getMinutes()).padStart(2, '0');
    var ss = String(today.getSeconds()).padStart(2, '0');

    this.excelService.generateExcel(report, `Reporte, Registro Civil, Buscar Acto - ${mm}-${dd}-${yyyy} ${hh}.${ii}.${ss}`);
  };

  public getLastFourMondays(): Date[] {
    const mondays: Date[] = [];
    const today = new Date();

    // Ajustar la fecha al lunes de esta semana
    const dayOfWeek = today.getDay();
    const daysToMonday = (dayOfWeek + 6) % 7; // Lunes es 0
    const firstMonday = new Date(today);
    firstMonday.setDate(today.getDate() - daysToMonday);

    // Agregar los lunes de las últimas cuatro semanas
    for (let i = 0; i < 15; i++) {
      const monday = new Date(firstMonday);
      monday.setDate(firstMonday.getDate() - i * 7);
      mondays.push(monday);
    }

    return mondays;
  }

  public getWeekDateRange(date: Date | null = null): string[] {
    // Si no se proporciona fecha, usar la fecha actual
    const referenceDate = date ? new Date(date) : new Date();

    // Obtener el día de la semana (0 = domingo, 1 = lunes, ..., 6 = sábado)
    const dayOfWeek = referenceDate.getDay();

    // Calcular días hasta el próximo sábado (inicio de semana)
    const daysToSubtract = dayOfWeek === 6 ? 0 : dayOfWeek === 0 ? 1 : dayOfWeek + 1;

    // Crear una copia de la fecha de referencia para no modificarla
    const startDate = new Date(referenceDate);
    startDate.setDate(referenceDate.getDate() - daysToSubtract);

    // Calcular la fecha de fin (viernes) sumando 6 días al inicio
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);

    // Formatear las fechas en formato YYYY-MM-DD
    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Los meses son 0-indexados
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}/${month}/${day}`;
    };

    return [formatDate(startDate), formatDate(endDate)];
  }
}
